import { Helmet, HelmetData } from "react-helmet-async";
import { useState } from "react";
import toast from "react-hot-toast";
import { Contact_Img } from "../../images";
import { EMAIL_SERVICE, INTERNAL_RECIPIENTS } from "../../utils/constants";
import { NewFooter } from "../newFooter";
import { sendFormSubmissionDetails } from "../../utils/templates";
import CryptoJS from "crypto-js";
import JSEncrypt from "jsencrypt";

const helmetData = new HelmetData({});

export default function ContactUs() {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const form = new FormData(e.target);
    const payload = {
      ...Object.fromEntries(form),
    };

    try {
      // 1. Fetch RSA public key
      const res = await fetch(EMAIL_SERVICE + "public-key");
      const publicKey = await res.text();

      // 2. Generate random AES key & IV
      const aesKey = CryptoJS.lib.WordArray.random(32); // 256-bit
      const iv = CryptoJS.lib.WordArray.random(16);

      const payloadData = {
        to: INTERNAL_RECIPIENTS.join(", "),
        senderName: "AI Global Website",
        from: "no-reply@dataassistant.ai",
        subject: "Contact Us Form AI Africa",
        body: sendFormSubmissionDetails(payload, "Contact Us Form AI Africa"),
        messageType: "html",
      };

      const encryptedPayload = CryptoJS.AES.encrypt(
        JSON.stringify(payloadData).toString(),
        aesKey,
        {
          iv: iv,
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        },
      ).toString();

      // 4. Encrypt AES key with RSA public key
      const encrypt = new JSEncrypt();

      encrypt.setPublicKey(publicKey);
      const encryptedAesKey = encrypt.encrypt(
        aesKey.toString(CryptoJS.enc.Base64),
      );

      if (!encryptedPayload) {
        throw new Error("Failed to encrypt message. Please try again later.");
      }

      // Send encrypted payload to email service
      const response = await fetch(EMAIL_SERVICE + "send/encrypted", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          encryptedAesKey,
          iv: iv.toString(CryptoJS.enc.Base64),
          encryptedPayload,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send encrypted email");
      }

      handleSuccess(
        "🎉 We’ve received your message and will get back to you soon.",
      );
      e.target.reset();
    } catch (error) {
      toast.error("An error occurred. Please try again later.");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccess = (message) => {
    toast.dismiss();
    toast.success(message, {
      duration: 3000,
      position: "top-center",
      style: {
        background: "#2F4171",
        color: "#fff",
        fontWeight: "bold",
      },
    });
  };

  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Contact Analytics Intelligence Africa | Get in Touch | Enterprise AI Africa</title>
        <link rel="canonical" href="https://africa.analyticsintelligence.com/contact" />
        <meta
          name="description"
          content="Contact Analytics Intelligence Africa for inquiries, support, or partnership opportunities. Reach out to our team to learn more about our AI, data annotation, model evaluation, and enterprise analytics services across Africa."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence Africa contact, contact AI company Africa, AI company Nigeria contact, AI company Ghana contact, AI company Kenya contact, enterprise AI Africa inquiry, data analytics Africa contact, AI partnership Africa, AI services Africa, get in touch Analytics Intelligence Africa"
        />
        <meta property="og:title" content="Contact Analytics Intelligence Africa | Enterprise AI & Data Services" />
        <meta property="og:description" content="Get in touch with Analytics Intelligence Africa for AI, data annotation, model evaluation, and enterprise analytics inquiries across Africa." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact Analytics Intelligence Africa | Enterprise AI & Data Services" />
        <meta name="twitter:description" content="Get in touch with Analytics Intelligence Africa for AI, data annotation, model evaluation, and enterprise analytics inquiries across Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "url": "https://africa.analyticsintelligence.com/contact",
            "name": "Contact Analytics Intelligence Africa",
            "description": "Contact Analytics Intelligence Africa for AI, data annotation, model evaluation, and enterprise analytics services across Africa.",
            "mainEntity": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com",
              "contactPoint": {
                "@type": "ContactPoint",
                "contactType": "customer support",
                "areaServed": ["Nigeria", "Ghana", "Kenya", "South Africa", "Rwanda", "Africa"]
              }
            }
          }
        `}</script>
      </Helmet>

      <main>
        <section className="min-h-[95vh] flex items-center bg-[#0E2F5D]">
          <div className="container mx-auto w-full h-full flex flex-col gap-16 pt-[96px] md:pt-[15rem] pb-[96px]">
            <div className="flex flex-col items-center gap-4">
              <h2 className="text-[36px] md:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                Contact Us
              </h2>

              <p className="text-sm md:text-lg text-center font-medium text-[#E3E3E3] leading-5 md:max-w-[1200px]">
                Get in touch with us for any inquiries or support regarding
                Analytics Intelligence.
              </p>
            </div>

            <div className="flex flex-col md:flex-row items-center gap-11 bg-[#FFFFFF0D] p-4 md:p-16">
              <div className="basis-full md:basis-2/4 flex items-center justify-center">
                <img
                  src={Contact_Img}
                  alt="Vision and Mission"
                  className="w-full max-w-[400px] h-auto"
                />
              </div>

              <div className="w-full basis-full md:basis-2/4 space-y-4 flex items-center justify-center ">
                <form
                  onSubmit={handleSubmit}
                  className="w-full max-w-full md:max-w-lg space-y-[36px]"
                >
                  {/* Name */}
                  <div className="flex flex-col gap-3">
                    <label
                      htmlFor="name"
                      className="block font-bold text-white"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter name"
                      className="w-full rounded-md bg-[#FFFFFF33] border border-[#FFFFFF66] px-2 md:px-6 py-3 text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Email */}
                  <div className="flex flex-col gap-3">
                    <label
                      htmlFor="email"
                      className="block font-bold text-white"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter email"
                      className="w-full rounded-md bg-[#FFFFFF33] border border-[#FFFFFF66] px-2 md:px-6 py-3 text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-3">
                    <label
                      htmlFor="message"
                      className="block font-bold text-white"
                    >
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      placeholder="Type message here"
                      className="w-full rounded-md bg-[#FFFFFF33] border border-[#FFFFFF66] px-6 py-3 text-white placeholder-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Submit button */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-[#0073FF] text-white py-3 font-semibold hover:bg-blue-700 transition"
                  >
                    {submitting ? "Submitting...." : "Submit"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
        <NewFooter />
      </main>
    </>
  );
}
